import 'maplibre-gl/dist/maplibre-gl.css'

import { Map as BaseMap, ScaleControl } from 'maplibre-gl'
import isEmpty from 'lodash-es/isEmpty'

import { authToken } from '@bikemap/js/api/auth-token'
import { MAPTILER_API_KEY, MAP_MAX_ZOOM, UNIT, DEFAULT_MAP_STYLE_URL, ARCGIS_API_KEY } from '@bikemap/js/settings'
import { EventEmitter, logError } from '@bikemap/js/utility'
import { handle3DTerrainChange } from '@bikemap/js/integrations/map/terrain'

// ResizeObserver is only present in modern browsers
// @ts-ignore
const ResizeObserver = window.ResizeObserver

const BIKEMAP_URL_IDENTIFIER = 'bikemap.net'
const MAPTILER_URL_IDENTIFIER = 'api.maptiler.com'
const ARCGIS_URL_IDENTIFIER = 'ibasemaps-api.arcgis.com'
const MAP_STYLE_URL_IDENTIFIER = 'mapstyle.bikemap.net'

/**
 * @typedef {Object} MapOptions
 * @property {{ center: import('@bikemap/js/map').LngLat, zoom: number }} viewport
 */

/**
 * A base map without additional controls or features.
 */
export default class Map extends EventEmitter {

    /** @type {boolean} */
    initialized = false

    /** @type {BaseMap} */
    _map

    /** @type {import("./features").MapFeature[]} */
    _preInitFeatures = []

    /**
     * Create a map inside the specified DOM element.
     * If no bounds are provided, the location option is used.
     * @param {string} [elementId=map] ID of the DOM element that should contain the map
     * @param {MapOptions} [options] Object of options to configure the map.
     */
    constructor(elementId = 'map', options = {}) {
        super()

        if (!this._validateOptions(options)) {
            return false
        }

        this._transformRequest = this._transformRequest.bind(this)
        this._onMapInitialized = this._onMapInitialized.bind(this)

        this._initMap(elementId, options)
    }

    /**
     * Add a feature to this map. This is a convenience utility if the feature shouldn't be
     * assigned to a variable outside.
     * @param {import("../features/MapFeature").MapFeature} feature
     * @returns {Map} this Map instance for chaining
     */
    addFeature(feature) {
        if (this.initialized) {
            feature.addTo(this._map)
        } else {
            this._preInitFeatures.push(feature)
        }
        return this
    }

    /**
     * Initialize the wrapped map instance.
     * @param {string} elementId
     * @param {MapOptions} options
     */
    async _initMap(elementId, options) {
        const {
            viewport,
        } = options

        // We need to have an access token before initializing to make sure our map style works
        await authToken.getAuthHeader()

        const baseMapOptions = {
            container: elementId,
            ...viewport,
            style: DEFAULT_MAP_STYLE_URL,
            // 'load' event sometimes doesn't fire if this is used, then it does when map is clicked
            transformRequest: this._transformRequest,
            maxZoom: MAP_MAX_ZOOM,
        }

        this._map = new BaseMap(baseMapOptions)

        this._map.once('load', this._onMapInitialized)

        // Size of map container can change when a CSS file arrives later or the screen is resized
        this._setupResizeObserver(elementId)
    }

    /**
     * Validate received options to make sure all required fields are set.
     *
     * @param {MapOptions} options
     * @return {boolean}
     */
    _validateOptions(options) {
        if (isEmpty(options.viewport)) {
            logError('[Map Error]', new Error('Option `viewport` is missing.'))
            return false
        }
        return true
    }

    /**
     * Transform requests triggered by the map.
     * @param {string} url
     * @returns {Object}
     */
    _transformRequest(url) {
        if (
            url.includes(BIKEMAP_URL_IDENTIFIER) &&
            !url.includes(MAP_STYLE_URL_IDENTIFIER)
        ) {
            return {
                url,
                headers: { Authorization: authToken.getCurrentAuthHeader() },
            }
        }

        if (url.includes(MAPTILER_URL_IDENTIFIER)) {
            return {
                url: url.split('?')[0] + '?key=' + MAPTILER_API_KEY,
            }
        }

        if (url.includes(ARCGIS_URL_IDENTIFIER)) {
            return {
                url: url.split('?')[0] + '?token=' + ARCGIS_API_KEY,
            }
        }

        return {
            url,
        }
    }

    /**
     * Called when the wrapped MapLibre map has been initialized.
     */
    _onMapInitialized() {
        this._addScaleControl()

        this.initialized = true

        this._preInitFeatures.forEach(feature => {
            feature.addTo(this._map)
        })
        this._preInitFeatures = []

        this._map.on('styledata', () => {
            handle3DTerrainChange(this._map)
        })
    }

    /**
     * Add the scale legend.
     */
    _addScaleControl() {
        this._map.addControl(new ScaleControl({
            maxWidth: 80,
            unit: UNIT,
        }), 'bottom-left')
    }

    /**
     * Optimize map dimensions when the container div changes size.
     * @param {string} elementId map container ID
     */
    _setupResizeObserver(elementId) {
        // Draft status, only supported in modern browsers
        if (ResizeObserver) {
            const mapContainer = document.getElementById(elementId)
            const resizeObserver = new ResizeObserver(() => {
                // Let the map adjust its size
                this._map.resize()
            })
            resizeObserver.observe(mapContainer)
        }
    }

}

export { Map }
